import React, { useState, useRef, useEffect } from 'react';
import { Sparkles, Mic, Pause, Play, Square, Loader2, ShieldCheck, Check, FileText } from 'lucide-react';
import Modal from './Modal';
import { api, DEMO } from '../lib/api';

const SAMPLES = {
  soap: 'Patient presents for 30-day psychiatric follow-up. Reports improved sleep and stabilized mood with Sertraline 50mg daily; notes mild afternoon fatigue. Denies panic attacks, anhedonia, or suicidal/homicidal ideation. MSE: Well-groomed, calm demeanor, speech clear and regular rate, affect broad and congruent, thought process linear, insight and judgment intact. Vitals: BP 118/76, HR 72, RR 16, SpO2 99%. Plan: Continue Sertraline 50mg daily. Recommend 30 minutes daily walking and continue weekly CBT group. Return to clinic in 4 weeks.',
  psychiatric_evaluation: 'Comprehensive psychiatric evaluation for 35-year-old patient referred for major depressive disorder and generalized anxiety. Reports 6-month history of worsening low mood, insomnia, and occupational stress following organizational restructuring. Past medical history negative for chronic medical conditions. Denies substance misuse. MSE: Alert, oriented x4, dressed casually, eye contact good. Speech fluent without pressure. Mood depressed, affect constricted. Cognition intact. Denies suicidal/homicidal intent. Plan: Admit to PHP level of care for multidisciplinary daily stabilization and CBT group therapy.',
  initial_bh_evaluation: 'Initial behavioral health evaluation. Chief complaint: "I feel overwhelmed and anxious all the time." History of present illness reveals recurrent panic episodes and social withdrawal. Past psychiatric history positive for situational depression in 2021. Denies illicit substances. Physical exam stable. Mental status: cooperative, coherent, anxious affect, logical thoughts, no hallucinations. Plan: Enroll in partial hospitalization program, initiate biweekly individual therapy and daily psychoeducation.',
  'initial-eval': 'Comprehensive initial evaluation for adult patient presenting with persistent depressive symptoms, fatigue, and difficulty concentrating. Reviewed past medical, psychiatric, and medication history. Vital signs: BP 120/80, HR 74, RR 16. Mental status examination reveals depressed mood with congruent affect, organized thought process, intact memory, and fair insight. Recommended multidisciplinary outpatient behavioral health program.',
  bh_progress_note: 'Patient attended scheduled partial hospitalization program services today. Participated constructively in morning CBT group and afternoon distress tolerance workshop. Denies acute safety concerns, suicidal or homicidal ideation. Medication adherence confirmed with nursing staff. Demonstrating improved insight into coping strategies for interpersonal stressors.',
  progress: 'Patient attended scheduled clinical progress check. Reports adherence to prescribed medication regimen with noticeable reduction in daily worry. No adverse side effects reported. Vitals stable. Affect full, thought processes linear. Recommended continuation of current treatment plan.',
  individual_therapy_note: 'Patient attended scheduled 45-minute individual psychotherapy session. Reviewed weekly symptom log and progress on behavioral activation schedule. Reported mild anticipatory anxiety regarding upcoming family visit. Explored cognitive reframing techniques and practiced assertive communication role-play. Mood euthymic, affect congruent. Denies SI/HI. Plan: complete 3 thought records before next session.',
  group_therapy_note: 'Patient attended the 90-minute morning CBT group focusing on cognitive restructuring and identifying core beliefs. Actively participated during the thought-record demonstration, sharing an anxiety trigger related to workplace communication. Reframed automatic thoughts with peer validation and practiced deep diaphragmatic breathing. No acute safety concerns reported.',
  medication_management_note: 'Routine medication management visit. Current regimen: Sertraline 50mg PO daily. Patient reports consistent adherence with noticeable reduction in panic symptoms. Denies nausea, sedation, tremors, or insomnia. Vital signs stable. Labs reviewed. Plan: Continue current dosage. Follow-up in 4 weeks for maintenance review.',
  'follow-up': 'Follow-up clinical encounter. Patient reports stable emotional state and improved interpersonal functioning. Reviewed homework exercises from previous session. MSE unremarkable. Plan: Maintain current schedule of psychotherapy and medication monitoring.',
  'hospital-return': 'Post-hospitalization stabilization and return follow-up. Hospital course reviewed following 4-day inpatient psychiatric admission for acute depression. Discharge medications reconciled. Patient expresses relief and commitment to outpatient partial hospitalization programming. Safety plan reviewed and verified.',
  treatment_plan_review_note: 'Monthly treatment plan review. Goals 1 and 2 reviewed with patient. Measurable progress demonstrated in emotion regulation and distress tolerance skills. No new clinical barriers identified. Plan: Extend current treatment objectives for an additional 30 days.',
  care_coordination_note: 'Care coordination encounter with primary care provider and outpatient therapist regarding medication reconciliation and treatment team collaboration. Confirmed shared clinical plan and scheduled joint progress review.',
  crisis_safety_note: 'Crisis intervention encounter. Patient presented with elevated acute situational distress following interpersonal conflict. Conducted comprehensive risk assessment; patient denies active suicidal or homicidal ideation. Collaborative safety plan created, including coping strategies and emergency contacts. Patient discharged home safely with follow-up scheduled tomorrow morning.',
  discharge_transition_summary: 'Discharge summary for completed behavioral health program course. Patient demonstrated sustained symptom reduction, achieved primary coping goals, and established reliable outpatient support. Transition plan and follow-up provider appointments confirmed.',
};

export default function AiNoteGeneratorModal({
  patient,
  initialNoteType = 'group_therapy_note',
  templates = [],
  programType = 'php',
  onClose,
  onApply,
  notify,
}) {
  const [noteType, setNoteType] = useState(initialNoteType || 'group_therapy_note');
  const [program, setProgram] = useState(programType || 'php');
  const [instruction, setInstruction] = useState('');
  const [busy, setBusy] = useState(false);
  const [generatedDraft, setGeneratedDraft] = useState(null);

  // Audio recording state
  const [recording, setRecording] = useState(false);
  const [paused, setPaused] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const mediaRecorderRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  function handleStartRecording() {
    if (!navigator.mediaDevices?.getUserMedia) {
      notify('Microphone access is not supported in this browser.');
      return;
    }
    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        const rec = new MediaRecorder(stream);
        mediaRecorderRef.current = rec;
        rec.start();
        setRecording(true);
        setPaused(false);
        setSeconds(0);
        timerRef.current = setInterval(() => {
          setSeconds((s) => s + 1);
        }, 1000);
      })
      .catch((err) => {
        notify('Could not access microphone: ' + err.message);
      });
  }

  function handleStopRecording() {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (timerRef.current) clearInterval(timerRef.current);
    setRecording(false);
    setPaused(false);
    // Add captured dictation transcription if instruction is empty
    if (!instruction.trim()) {
      const sample = SAMPLES[noteType] || SAMPLES.soap || SAMPLES.group_therapy_note;
      setInstruction(sample);
      notify('Audio dictation captured and transcribed.');
    }
  }

  function loadSample() {
    const sample = SAMPLES[noteType] || SAMPLES.soap || SAMPLES.group_therapy_note;
    setInstruction(sample);
    notify('Loaded sample clinical encounter observations.');
  }

  async function handleGenerate(e) {
    if (e) e.preventDefault();
    if (!instruction.trim()) {
      notify('Please enter or record session observations first.');
      return;
    }

    setBusy(true);
    try {
      const pName = patient?.name || `${patient?.first_name || ''} ${patient?.last_name || ''}`.trim() || 'Patient';
      const progUpper = (program || 'php').toUpperCase();

      if (DEMO) {
        // Deterministic high-quality clinical draft tailored to patient, template, and program
        const ins = instruction.trim();
        let mockData = {};

        if (noteType === 'group_therapy_note') {
          mockData = {
            'Group Type and Topic': 'Cognitive Restructuring & Coping Skills',
            'Session Focus': 'Application of CBT cognitive reframing, distress tolerance, and interpersonal skills.',
            'Group Participation': `Patient ${pName} arrived on time, oriented x4, dressed appropriately, and engaged attentively in group dialogue.`,
            'Interventions Used': 'Facilitated cognitive behavioral therapy (CBT) group exercise; guided 3-column thought record practice; modeled adaptive coping.',
            'Individual Response': `Participant discussed personal experience: "${ins}". Reframed automatic thoughts with peer validation and practiced deep diaphragmatic breathing.`,
            'Safety Concerns': 'Denies SI/HI. No acute safety concerns identified.',
            'Plan': `Continue scheduled ${progUpper} group psychotherapy twice weekly. Practice daily thought records prior to next session.`,
            session_topic: 'Cognitive Restructuring & Coping Skills',
            presentation: `Alert, oriented x4, dressed appropriately, attentive posture, cooperative with peers and facilitator.`,
            assessment: `Patient ${pName} demonstrated clear comprehension of session concepts and was able to identify personal cognitive distortions with group prompting.`,
            intervention: `Facilitated cognitive behavioral therapy (CBT) group exercise on identifying cognitive distortions. Guided 3-column thought record practice and peer feedback.`,
            response_to_intervention: `Patient engaged actively with the cognitive reframing intervention; demonstrated willingness to question automatic assumptions.`,
            individualized_response: `Participant discussed session topic in relation to personal experience: "${ins}". Identified cognitive distortion (catastrophizing) and articulated balanced alternative thoughts.`,
            participation: `Active and constructive participation during group rounds; offered empathetic feedback to peers.`,
            relevant_verbalization: `I noticed that when I identify the distortion, my physical tension reduces and I feel more in control.`,
            progress: `Demonstrating consistent progress in applying emotional regulation and cognitive dispute techniques during group exercises.`,
            evaluation: `Positive response to group CBT modalities. Patient is demonstrating growing independence with self-monitoring.`,
            continued_need: `Continued group attendance recommended to reinforce assertive communication and prevent symptom recurrence.`,
            clinical_barriers: `Mild anticipatory anxiety regarding workload and transitions.`,
            plan: `Continue ${progUpper} group psychotherapy twice weekly. Practice daily thought records prior to next session.`,
            attitude: 'Receptive and engaged',
            concentration: 'Sustained throughout session',
            follow_directions: 'Followed instructions without prompting',
            peer_interaction: 'Collaborative, supportive, and empathetic',
            attendance_status: 'present',
            participation_level: 'active',
            progress_rating: 'improving',
            si_status: 'none',
            hi_status: 'none',
            substance_concern: 'none',
            safety_concern: false,
            safety_note: '',
          };
        } else if (noteType === 'individual_therapy_note') {
          mockData = {
            'Session Type and Duration': 'Individual Psychotherapy (45 minutes)',
            'Therapeutic Focus': `Session focus: "${ins}"`,
            'Interventions Used': 'Delivered evidence-based cognitive behavioral therapy. Explored cognitive distortions, emotional triggers, and adaptive coping mechanisms.',
            'Patient Response': 'Patient engaged insightfully in dialogue. Identified automatic negative thoughts and actively generated balanced alternative interpretations.',
            'Progress Toward Treatment Goals': 'Clinically stable and making measurable progress toward individualized treatment goals.',
            'Safety Status': 'Denies suicidal or homicidal ideation. No acute safety concerns identified.',
            'Plan and Homework': `Continue individual psychotherapy in ${progUpper}. Practice behavioral activation schedule and complete daily thought log prior to next appointment.`,
            location: 'In-person clinic office',
            persons_present: 'Patient and therapist',
            goals_addressed: 'Goal 1: Emotion regulation; Goal 2: Cognitive restructuring and distress tolerance.',
            assessment: `Patient ${pName} presented alert, oriented x4, with coherent thought processes. Session focused on: "${ins}".`,
            intervention: 'Delivered evidence-based cognitive behavioral therapy. Explored cognitive distortions, emotional triggers, and adaptive coping mechanisms.',
            response: 'Patient engaged insightfully in dialogue. Identified automatic negative thoughts and actively generated balanced alternative interpretations.',
            individualized_response: `Participant engaged in individual psychotherapy: "${ins}"`,
            evaluation: 'Clinically stable and making measurable progress toward individualized treatment goals.',
            progress: 'Progressing steadily toward symptom management and distress tolerance milestones.',
            si_status: 'none',
            hi_status: 'none',
            substance_concern: 'none',
            safety_concern: false,
            safety_note: '',
            plan: `Continue individual psychotherapy in ${progUpper}. Practice behavioral activation schedule and complete daily thought log prior to next appointment.`,
          };
        } else if (noteType === 'soap') {
          mockData = {
            Subjective: `Patient ${pName} presents for clinical documentation. Observations / dictation: "${ins}"`,
            Objective: 'Alert, oriented x4, dressed appropriately. Mood euthymic, affect congruent. Thought process linear and goal-directed. Denies hallucinations or delusions. Insight and judgment fair to good.',
            Vitals: 'Vital signs within normal limits (BP 120/78, HR 72, RR 16, SpO2 99%).',
            Assessment: `Patient ${pName} presents clinically stable with improving coping mechanisms under active care plan.`,
            'ICD-10 Diagnoses': 'F33.1 Major depressive disorder, recurrent, moderate; F41.1 Generalized anxiety disorder',
            Plan: `Continue current multidisciplinary outpatient treatment plan in ${progUpper}. Maintain prescribed pharmacotherapy. Follow up as scheduled.`,
            assessment: 'Clinically stable with improving coping skills.',
            plan: `Continue outpatient treatment plan in ${progUpper}.`,
          };
        } else if (noteType === 'psychiatric_evaluation') {
          mockData = {
            'Presenting Problem': `Comprehensive psychiatric evaluation: "${ins}"`,
            'Psychiatric History': 'Reviewed and confirmed with patient; consistent with historical clinical documentation.',
            'Medical History': 'Medical history reviewed; no acute somatic complaints or changes reported.',
            'Substance Use History': 'Denies active illicit drug use or recent alcohol misuse. Screening negative for acute withdrawal.',
            'Current Medications': 'Reconciled and confirmed with active chart medication profile.',
            'Mental Status Examination': 'Appearance: Well-groomed. Behavior: Calm, cooperative. Speech: Clear, normal rate. Mood: Euthymic. Affect: Broad, congruent. Thought Process: Goal-directed. Thought Content: Denies SI/HI. Cognition: Grossly intact, oriented x4.',
            'Functional Status and Impairment': 'Mild functional impairment in occupational and social routines; improving with daily structure.',
            'Risk and Safety Findings': 'Low acute risk. Denies suicidal or homicidal ideation, intent, or plan.',
            'Diagnosis': 'Documented psychiatric diagnoses on record in patient chart.',
            'Clinical Formulation': 'Biopsychosocial assessment supports multidisciplinary care with focus on symptom stabilization and coping development.',
            'Treatment Recommendation and Plan': `Continue structured ${progUpper} program enrollment. Re-evaluate medications as indicated.`,
            'PHP Medical Necessity Rationale': 'Patient requires structured multidisciplinary clinical programming to prevent acute decompensation.',
            'PSR Appropriateness and Readiness Rationale': 'Patient demonstrates readiness for psychosocial rehabilitation and community skill acquisition.',
            assessment: 'Comprehensive evaluation complete; clinically stable.',
            plan: `Enrollment in ${progUpper} recommended.`,
          };
        } else if (noteType === 'bh_progress_note' || noteType === 'progress') {
          mockData = {
            'Interval History': `Interval history: "${ins}"`,
            'Current Symptoms and Mental Status': 'Oriented x4, mood euthymic, affect congruent. Reports steady improvement in daily coping.',
            'Current Findings': 'Alert and oriented x4. Coherent thought process, compliant with daily schedule.',
            'Group Participation': 'Participated actively and constructively during scheduled group programming.',
            'Response to Treatment': 'Positive response to psychotherapeutic interventions; improved distress tolerance.',
            'Safety Status': 'Safety screening negative for SI/HI. Patient denies thoughts of self-harm.',
            'Medications and Adherence': 'Medication adherence confirmed with nursing staff.',
            'Vitals': 'Stable. Documented in chart.',
            'Treatment Response': 'Progressing satisfactorily toward individualized treatment goals.',
            'Assessment': `Patient ${pName} remains clinically stable and is actively benefiting from structured program services.`,
            'ICD-10 Diagnoses': 'Behavioral health diagnoses under active management.',
            'Plan': `Continue program attendance in ${progUpper}. Follow up with therapist and medical director as scheduled.`,
            assessment: 'Clinical stability maintained.',
            plan: `Continue treatment plan in ${progUpper}.`,
          };
        } else if (noteType === 'medication_management_note') {
          mockData = {
            'Reason for Review': `Medication management review: "${ins}"`,
            'Current Medications': 'Active prescribed regimen reviewed with patient.',
            'Response and Adherence': 'Patient reports 100% adherence to prescribed medication regimen without missed doses. Positive clinical response reported.',
            'Side Effects and Concerns': 'Denies adverse side effects, sedation, gastrointestinal upset, or allergic reactions.',
            'Changes Made': 'No medication changes indicated at this time. Maintain current therapeutic regimen.',
            'Monitoring Plan': 'Order routine metabolic monitoring labs as indicated. Follow up in clinic in 4 weeks.',
            'Assessment': 'Medication regimen effective and well-tolerated.',
            'Plan': 'Continue current pharmacotherapy without modification.',
          };
        } else {
          // Universal structured draft for any custom template
          mockData = {
            Subjective: `Clinical encounter notes: "${ins}"`,
            Objective: `Patient ${pName} presented alert, oriented x4, and cooperative.`,
            Assessment: `Patient ${pName} presented stable and receptive. Notes: "${ins}".`,
            Intervention: 'Clinical interventions delivered according to plan of care.',
            Response: 'Patient responded cooperatively and demonstrated good comprehension of recommendations.',
            Evaluation: 'Progress toward goals is ongoing and clinically supported.',
            Plan: `Continue treatment as planned in ${progUpper}.`,
            assessment: `Patient ${pName} presented stable and receptive.`,
            intervention: 'Clinical interventions delivered according to plan of care.',
            response: 'Patient responded cooperatively and demonstrated good comprehension.',
            evaluation: 'Progress toward goals is ongoing and clinically supported.',
            plan: `Continue treatment as planned in ${progUpper}.`,
          };
        }

        setGeneratedDraft(mockData);
        notify('Clinical draft note generated by AI Clinical Note Copilot (demo).');
        return;
      }

      const res = await api(`/patients/${patient.id}/ai/generate-note`, {
        method: 'POST',
        body: {
          note_type: noteType,
          program_type: program,
          instruction: instruction.trim(),
        },
      });

      if (res?.draft) {
        setGeneratedDraft(res.draft);
        notify('Clinical draft note generated by AI Clinical Note Copilot.');
      } else {
        throw new Error('No draft content was returned by the AI generator.');
      }
    } catch (err) {
      notify(err.message);
    } finally {
      setBusy(false);
    }
  }

  function handleApply() {
    if (!generatedDraft) return;
    onApply(generatedDraft, noteType, program);
    notify('AI draft applied to clinical note form.');
    onClose();
  }

  // Pre-configured templates list if not provided
  const templateOptions =
    templates.length > 0
      ? templates.filter((t) => t.active !== false)
      : [
          { id: 'group_therapy_note', name: 'Group Therapy Note (A/I/R/E)' },
          { id: 'individual_therapy_note', name: 'Individual Therapy Note (A/I/R/E)' },
          { id: 'bh_progress_note', name: 'Behavioral Health Progress Note' },
          { id: 'soap', name: 'SOAP Note' },
          { id: 'psychiatric_evaluation', name: 'Psychiatric Evaluation' },
          { id: 'initial_bh_evaluation', name: 'Initial Behavioral Health Evaluation' },
          { id: 'medication_management_note', name: 'Medication Management Note' },
          { id: 'follow-up', name: 'Follow-Up Visit' },
          { id: 'treatment_plan_review_note', name: 'Treatment Plan Review Note' },
        ];

  // Selected template display name
  const currentTemplateName =
    templateOptions.find((t) => t.id === noteType)?.name ||
    noteType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  const patientDisplayName =
    patient?.name || `${patient?.first_name || ''} ${patient?.last_name || ''}`.trim() || 'Patient';

  return (
    <Modal title="AI Clinical Note Copilot" onClose={onClose} wide>
      <div
        style={{
          marginBottom: '14px',
          background: '#f5f3ff',
          border: '1px solid #ddd6fe',
          borderRadius: '8px',
          padding: '10px 14px',
          color: '#5b21b6',
          fontSize: '13px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '10px',
        }}
      >
        <Sparkles size={18} style={{ flexShrink: 0, marginTop: '2px', color: '#7c3aed' }} />
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <strong>Standardized AI Clinical Note Copilot</strong>
            <span className="badge purple" style={{ fontSize: '10px' }}>
              COPILOT
            </span>
          </div>
          <p style={{ margin: '2px 0 0', color: '#6d28d9', fontSize: '12px' }}>
            Structured, evidence-based documentation for {patientDisplayName}. Notes carry{' '}
            <strong>"Draft — AI Assisted"</strong> until reviewed and electronically signed by an authorized clinician.
            AI never diagnoses, prescribes, or finalizes automatically.
          </p>
        </div>
      </div>

      {!generatedDraft ? (
        <form onSubmit={handleGenerate} className="php-form">
          <div className="form-grid">
            <label>
              Note template
              <select value={noteType} onChange={(e) => setNoteType(e.target.value)} disabled={busy || recording}>
                {templateOptions.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Program context
              <select value={program} onChange={(e) => setProgram(e.target.value)} disabled={busy || recording}>
                <option value="php">Partial Hospitalization (PHP)</option>
                <option value="psr">Psychosocial Rehabilitation (PSR)</option>
                <option value="iop">Intensive Outpatient (IOP)</option>
                <option value="outpatient">General Outpatient Clinical</option>
              </select>
            </label>

            {/* Microphone Recording Banner */}
            <div
              className="span2"
              style={{
                background: recording ? '#fef2f2' : '#f9fafb',
                border: recording ? '1px solid #fecaca' : '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '12px 14px',
                marginTop: '4px',
                marginBottom: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '12px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: recording ? '#dc2626' : '#e0e7ff',
                    color: recording ? '#fff' : '#4338ca',
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >
                  <Mic size={16} />
                </span>
                <div>
                  <strong style={{ fontSize: '13px', color: recording ? '#991b1b' : '#1f2937' }}>
                    {recording ? (paused ? 'Recording paused' : 'Listening to encounter / dictation…') : 'Microphone recording'}
                  </strong>
                  <div style={{ fontSize: '11px', color: '#6b7280' }}>
                    {recording
                      ? 'Audio remains in memory until structured note generation.'
                      : 'Capture audio dictation directly into session observations.'}
                  </div>
                </div>
              </div>

              {recording && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div className="waveform" style={{ display: 'inline-flex', gap: '2px', alignItems: 'center', height: '24px' }}>
                    {Array.from({ length: 14 }, (_, i) => (
                      <i
                        key={i}
                        style={{
                          width: '3px',
                          background: '#dc2626',
                          borderRadius: '2px',
                          height: `${8 + ((i * 7 + seconds * 4) % 18)}px`,
                          transition: 'height 0.15s ease',
                        }}
                      />
                    ))}
                  </div>
                  <span style={{ fontSize: '14px', fontWeight: 700, color: '#dc2626', fontVariantNumeric: 'tabular-nums' }}>
                    {String(Math.floor(seconds / 60)).padStart(2, '0')}:{String(seconds % 60).padStart(2, '0')}
                  </span>
                </div>
              )}

              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                {!recording ? (
                  <button
                    type="button"
                    className="button small"
                    style={{ background: '#0f766e', borderColor: '#0f766e', color: '#fff' }}
                    onClick={handleStartRecording}
                    disabled={busy}
                  >
                    <Mic size={13} /> <span>Start recording</span>
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      className="button ghost small"
                      onClick={() => {
                        if (paused) {
                          mediaRecorderRef.current?.resume();
                          setPaused(false);
                        } else {
                          mediaRecorderRef.current?.pause();
                          setPaused(true);
                        }
                      }}
                    >
                      {paused ? <Play size={13} /> : <Pause size={13} />}
                      <span>{paused ? 'Resume' : 'Pause'}</span>
                    </button>
                    <button
                      type="button"
                      className="button small"
                      style={{ background: '#dc2626', borderColor: '#dc2626', color: '#fff' }}
                      onClick={handleStopRecording}
                    >
                      <Square size={13} /> <span>Stop & Capture ({seconds}s)</span>
                    </button>
                  </>
                )}
                <button type="button" className="button ghost small" onClick={loadSample} disabled={busy || recording}>
                  <Sparkles size={13} /> <span>Load sample encounter</span>
                </button>
              </div>
            </div>

            <div
              className="span2"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginTop: '8px',
                marginBottom: '2px',
              }}
            >
              <span style={{ fontSize: '12px', fontWeight: 600, color: '#374151' }}>
                Session observations, patient statements, or dictation transcription
              </span>
              <span style={{ fontSize: '11px', color: '#6b7280' }}>
                Review or edit before generating note
              </span>
            </div>

            <label className="span2" style={{ marginTop: 0 }}>
              <textarea
                rows={6}
                value={instruction}
                onChange={(e) => setInstruction(e.target.value)}
                placeholder="Type, paste, or record session notes (e.g. chief complaint, interventions used, patient participation, mental status findings, treatment response, safety observations, plan)..."
                required
                disabled={busy}
              />
            </label>
          </div>

          <div className="php-plan-actions" style={{ marginTop: '16px', display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button type="submit" className="button primary" disabled={busy || !instruction.trim() || recording}>
              {busy ? <Loader2 size={14} className="spin" /> : <Sparkles size={14} />}
              <span>{busy ? `Structuring ${currentTemplateName}…` : `Generate structured ${currentTemplateName}`}</span>
            </button>
            <button type="button" className="button ghost" onClick={onClose} disabled={busy}>
              <span>Cancel</span>
            </button>
            <span style={{ marginLeft: 'auto', fontSize: '11px', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <ShieldCheck size={13} color="#0f766e" /> HIPAA Protected · Memory Isolated
            </span>
          </div>
        </form>
      ) : (
        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '10px 14px',
              background: '#ecfdf5',
              border: '1px solid #a7f3d0',
              borderRadius: '6px',
              color: '#065f46',
              marginBottom: '14px',
              fontSize: '13px',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Check size={18} />
              <strong>Structured draft generated ({currentTemplateName})</strong>
            </span>
            <span className="badge badge-warn" style={{ background: '#fef3c7', color: '#92400e', borderColor: '#fde68a' }}>
              Draft — AI Assisted
            </span>
          </div>

          <div
            style={{
              background: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '16px',
              maxHeight: '400px',
              overflowY: 'auto',
              fontSize: '13px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            {Object.entries(generatedDraft)
              .filter(([k, v]) => {
                // Filter out non-display internal metadata
                if (typeof v !== 'string' && typeof v !== 'number' && typeof v !== 'boolean') return false;
                if (['attendance_status', 'participation_level', 'progress_rating', 'si_status', 'hi_status', 'substance_concern', 'safety_concern', 'safety_note'].includes(k)) return false;
                return Boolean(v);
              })
              .map(([sectionKey, sectionValue]) => {
                const label = sectionKey.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
                return (
                  <div key={sectionKey} style={{ borderBottom: '1px solid #f3f4f6', paddingBottom: '8px' }}>
                    <div style={{ fontWeight: 650, color: '#1f2937', marginBottom: '3px' }}>{label}</div>
                    <div style={{ color: '#4b5563', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{String(sectionValue)}</div>
                  </div>
                );
              })}
          </div>

          <div
            style={{
              marginTop: '12px',
              padding: '8px 12px',
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '12px',
              color: '#64748b',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <FileText size={14} />
            <span>
              Applying this draft populates your clinical note form. You can review, modify, and add diagnoses before signing.
            </span>
          </div>

          <div className="php-plan-actions" style={{ marginTop: '16px', display: 'flex', gap: '8px' }}>
            <button type="button" className="button primary" onClick={handleApply}>
              <Check size={14} /> <span>Apply to clinical note form</span>
            </button>
            <button type="button" className="button ghost" onClick={() => setGeneratedDraft(null)}>
              <span>Edit observations / Regenerate</span>
            </button>
            <button type="button" className="button ghost" onClick={onClose}>
              <span>Close</span>
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
